/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { logger } from '@dw/services/logger';
import { THttpMethod, HttpClientError } from '@dw/models/shared';
import { useAppSettings, useSnackbar, useSession, useActiveEnrolmentYear } from '@dw/hooks';
import { CONNECTION_ERROR_MESSAGE, DEFAULT_ERROR_MESSAGE, ROUTES } from '@dw/constants';
import { cleanJsonResponse, formatQueryParamString } from '@dw/utils';
import { shouldRedirectToLogin } from '@dw/utils/authFailure';

type TQueryParams = {[key: string]: any};


interface IHttpClientRequestOptions {
    /**
     * Should the user be logged out if API returns 401 or 403 errors. Defaults to `true`
     */
    autoLogOutOnAuthError?: boolean;

    /**
     * Should the error message be shown by default. Defaults to `true`
     */
    autoShowErrorMessages?: boolean;

    /**
     * Extra headers merged into the request (e.g. the examiner surface's X-Examiner-Token).
     * Merged BEFORE the Authorization header is applied, so a caller can never override the
     * session's bearer token with this.
     */
    customHeaders?: Record<string, string>;

    /**
     * Response properties holding free text, which must never be revived as dates however they look
     * (see `cleanJsonResponse`) — e.g. `['text']` for a note whose whole text is "2026-11-01".
     */
    preserveStringKeys?: readonly string[];
}

interface IHttpClientContext {
    POST: <T>(url: string, body?: any, file?: File, options?: IHttpClientRequestOptions) => Promise<T>;
    PUT: <T>(url: string, body?: any, options?: IHttpClientRequestOptions) => Promise<T>;
    DELETE: <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions) => Promise<T>;
    GET: <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions) => Promise<T>;
    buildUrl: (url: string, queryParams?: TQueryParams) => string;
}
const initialState: IHttpClientContext = {
    POST: () => Promise.resolve<any>({}),
    PUT: () => Promise.resolve<any>({}),
    DELETE: () => Promise.resolve<any>({}),
    GET: () => Promise.resolve<any>({}),
    buildUrl: () => ''
}
export const HttpClientContext = React.createContext<IHttpClientContext>(initialState);

const extractErrorMessages = (responseBody: any): string[] => {
    if (responseBody.errors && typeof responseBody.errors === 'object' && !Array.isArray(responseBody.errors) && Object.keys(responseBody.errors).length > 0) {
        const validErrors = Object.keys(responseBody.errors).filter(x => !x.startsWith('$'));
        if (validErrors.length > 0) {
            return validErrors.flatMap(key => responseBody.errors[key]);
        }
    } else if (responseBody.detail) {
        return [responseBody.detail];
    } else if (responseBody.message) {
        return [responseBody.message];
    }

    return [DEFAULT_ERROR_MESSAGE];
};

/**
 * ONLY 401 - the API saying the credential itself was not accepted.
 *
 * 403 is deliberately NOT here. It means "we know exactly who you are, and this is not yours": a
 * verdict on the request, never on the session. Treating it as an auth error signed a user out for
 * opening a page their role cannot see, and refreshed - rotated - a perfectly good token on the way
 * out. Worse, that teardown is local only, so the API went on holding the 90 day refresh token,
 * live and unrevoked, while the user was sent back to an email code. A 403 now surfaces as an
 * ordinary error the caller can show.
 */
const isUnauthenticated = (statusCode: number): boolean => statusCode === 401;

const getHttpClientRequestOptions = (options?: IHttpClientRequestOptions): IHttpClientRequestOptions => {
    const defaultOptions: IHttpClientRequestOptions = {
        autoLogOutOnAuthError: true,
        autoShowErrorMessages: true
    };

    return {
        ...defaultOptions,
        ...(options ?? {})
    }
}

async function downloadResponseAsFile<T>(response: Response): Promise<T> {
    const responseHeaders = Object.fromEntries(response.headers.entries());
    const contentDispositionParts = responseHeaders['content-disposition'].split(';').map(x => x.trim());
    const fileNameParts = contentDispositionParts.find(x => x.toLowerCase().startsWith('filename='))?.split('=') ?? []; 

    const blobResponse = await response.blob();
    const url = window.URL.createObjectURL(blobResponse);
    const a = document.createElement('a');
    a.href = url;
    if (fileNameParts.length > 0) {
        a.download = fileNameParts[fileNameParts.length - 1].replaceAll('"', '');
    }
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();    
    a.remove();

    return { message: 'File download will begin shortly' } as T
}

interface IHttpClientContextProviderProps {
    children: React.ReactNode;
}
export const HttpClientContextProvider: FunctionComponent<IHttpClientContextProviderProps> = (props: IHttpClientContextProviderProps) => {

    const { appSettings } = useAppSettings();
    const { showError } = useSnackbar();
    const { children } = props;

    const { getAccessToken, refreshAccessToken, hasLiveSession, signOut } = useSession();
    const { activeEnrolmentYear } = useActiveEnrolmentYear();

    const buildUrl = useCallback((url: string, params?: TQueryParams): string => {
        const queryString = formatQueryParamString(params);
        return `${url.startsWith('http') ? '' : appSettings.apiBaseUrl}${url}${queryString}`;
    }, [appSettings]);

    const { timezoneName, timezoneOffset } = useMemo(() => ({
        timezoneOffset: new Date().getTimezoneOffset() * -1,
        timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone
    }), []);

    const buildFetchOptions = useCallback((method: THttpMethod, accessToken: string | null, body?: any, file?: File, customHeaders?: Record<string, string>): RequestInit => {
        const options: RequestInit = {
            method,
            credentials: 'include',
            // Authenticated, user-scoped JSON must never be served from the browser's HTTP cache.
            // React Query is the caching layer; a cached GET silently defeats polling, so a
            // refetch would return byte-identical stale data and the UI would never update.
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                'X-Timezone-Offset': timezoneOffset.toString(),
                'X-Timezone-Name': timezoneName,
                'X-Active-Enrolment-Year': activeEnrolmentYear.toString(),
            }
        };

        if (file) {
            options.headers = {};
        }

        if (customHeaders) {
            options.headers = {
                ...options.headers,
                ...customHeaders
            };
        }

        if (accessToken) {
            options.headers = {
                ...options.headers,
                Authorization: `Bearer ${accessToken}`
            };
        }

        if (!file) {
            options.body = JSON.stringify(body);
        } else if (file) {
            const formData = new FormData();
            formData.append('file', file);
            if (body) {
                formData.append('request', JSON.stringify(body));
            }
            options.body = formData;
        }
        return options;
    }, [timezoneName, timezoneOffset, activeEnrolmentYear]);

    const execute = useCallback(async function <T>(method: THttpMethod, url: string, queryParams?: TQueryParams, body?: any, file?: File, options?: IHttpClientRequestOptions): Promise<T> {
        const fetchUrl = buildUrl(url, queryParams);
        const httpOptions = getHttpClientRequestOptions(options);

        try {
            const accessToken = await getAccessToken();

            // A session we STILL HOLD that could not produce an access token means the refresh never
            // got an answer - offline, a blip, a 5xx, a laptop whose Wi-Fi has not reconnected yet.
            // Sending the request anyway drops the Authorization header, earns a 401, and the block
            // below would then end a session the API never refused.
            //
            // That is how "Remember this device" users lost a 90 day token every morning: the first
            // requests after the machine woke raced the network, the refresh failed transiently,
            // every one of those requests came back 401, and the session was wiped locally. It left
            // no trace server side, because the refresh token never reached the API - which is why
            // the token rows sat there afterwards live, unrevoked and unreachable.
            //
            // `Session.context` already refuses to end a session over a transient refresh failure;
            // this is the other half of that promise, and without it that one was worthless.
            if (!accessToken && hasLiveSession() && httpOptions.autoLogOutOnAuthError) {
                throw new HttpClientError({
                    statusCode: 503,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    errorMessages: [CONNECTION_ERROR_MESSAGE]
                });
            }

            let fetchResponse = await fetch(fetchUrl, buildFetchOptions(method, accessToken, body, file, httpOptions.customHeaders));

            // An access token can lapse mid-session (or be rejected right after a rotation). One
            // de-duped refresh and a single retry keeps the user where they are instead of bouncing
            // them to login. Skipped for surfaces that send no bearer token (the public invoice
            // view) and for those that opted out of auth-error handling: an examiner's 401 on their
            // X-Examiner-Token says nothing about the staff session that may be open in the same
            // browser, and rotating that user's refresh token over it would be plain wrong.
            if (isUnauthenticated(fetchResponse.status) && accessToken && httpOptions.autoLogOutOnAuthError) {
                const refreshedAccessToken = await refreshAccessToken();
                if (refreshedAccessToken) {
                    fetchResponse = await fetch(fetchUrl, buildFetchOptions(method, refreshedAccessToken, body, file, httpOptions.customHeaders));
                } else if (hasLiveSession()) {
                    // The refresh could not be COMPLETED, but it was not refused either - the
                    // session is still ours, so this 401 is not a verdict on it. Same reasoning as
                    // the guard above: transient, not terminal. If the refresh HAD been refused,
                    // `Session.context` would already have cleared the session and this falls
                    // through to the teardown below, which is what a genuinely dead token deserves.
                    throw new HttpClientError({
                        statusCode: 503,
                        requestMethod: method,
                        requestUrl: fetchUrl,
                        errorMessages: [CONNECTION_ERROR_MESSAGE]
                    });
                }
            }

            if (isUnauthenticated(fetchResponse.status)) {
                // Only a session that no longer EXISTS sends anyone to the login screen, and
                // `Session.context` is what decides that: it clears the session when a refresh is
                // actively rejected, and leaves it alone for anything else. So the session is read
                // back here, after the attempt, rather than inferring death from the status code.
                if (shouldRedirectToLogin({
                    autoLogOutOnAuthError: httpOptions.autoLogOutOnAuthError,
                    hasLiveSession: hasLiveSession()
                })) {
                  // Tear the session down BEFORE navigating. The reload has to land on a real sign-in
                  // form: leaving the tokens and the bootstrap snapshot in place would let the login
                  // page hydrate as "authenticated" and send the user straight back to a dashboard
                  // the API is refusing to serve — a reload loop nobody can escape.
                  signOut();
                  window.location.href = ROUTES.Login;
                }

                throw new HttpClientError({
                    statusCode: fetchResponse.status,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    errorMessages: ['Fetch returned authentication error']
                });
            }

            const responseHeaders = Object.fromEntries(fetchResponse.headers.entries());
            if (responseHeaders['content-disposition'] && responseHeaders['content-disposition'].indexOf('attachment') >= 0) {
                return await downloadResponseAsFile(fetchResponse);
            }

            const response = await fetchResponse.json();
            if (fetchResponse.ok) {
                cleanJsonResponse(response, httpOptions.preserveStringKeys);
            }

            if (!fetchResponse.ok) {
                throw new HttpClientError({
                    statusCode: fetchResponse.status,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    rawErrors: response.errors,
                    errorMessages: extractErrorMessages(response)
                });
            }

            return response as T;
        } catch (err: any) {

            let httpClientError: HttpClientError;

            if (err instanceof HttpClientError) {
                httpClientError = err;
            } else {
                httpClientError = new HttpClientError({
                    statusCode: 500,
                    requestMethod: method,
                    requestUrl: fetchUrl,
                    errorMessages: [DEFAULT_ERROR_MESSAGE],
                    rawErrors: err
                });

                const errorMessage = (err.message ?? '') as string;
                const errorStack = (err.stack?.toString() ?? '') as string;
                if (!errorMessage.toLowerCase().includes('load failed') && !errorStack.toLowerCase().includes('failed to fetch')) {
                    logger.error(`Error while performing ${method} to "${fetchUrl}"`, httpClientError.errorResponse);
                }
            }

            if (httpOptions.autoShowErrorMessages) {
                showError(httpClientError.firstErrorMessage);
            }

            throw httpClientError;
        }
    }, [buildUrl, buildFetchOptions, getAccessToken, refreshAccessToken, hasLiveSession, signOut, showError]);

    const POST = useCallback(async function <T>(url: string, body?: any, file?: File, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('POST', url, undefined, body, file, options);
    }, [execute]);

    const PUT = useCallback(async function <T>(url: string, body?: any, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('PUT', url, undefined, body, undefined, options);
    }, [execute]);

    const DELETE = useCallback(async function <T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('DELETE', url, queryParams, undefined, undefined, options);
    }, [execute]);

    const GET = useCallback(async function GET<T>(url: string, queryParams?: TQueryParams, options?: IHttpClientRequestOptions): Promise<T> {
        return await execute('GET', url, queryParams, undefined, undefined, options);
    }, [execute]);

    const client = useMemo(() => ({
        POST,
        GET,
        DELETE,
        PUT,
        buildUrl
    }), [POST, GET, DELETE, PUT, buildUrl]);

    return (
        <HttpClientContext.Provider value={client}>
            {children}
        </HttpClientContext.Provider>
    )
};

